import React, { useEffect, useRef, useState } from 'react'
import './NetworkLoadingScreen.css'

export default function NetworkLoadingScreen({ networkIcon, networkName, onComplete }) {
  const canvasRef = useRef(null)
  const [fadeOut, setFadeOut] = useState(false)
  const animationRef = useRef(null)
  const timersRef = useRef({ fade: null, complete: null })
  const initializedRef = useRef(false)
  const completedRef = useRef(false)

  // Get theme colors from CSS variables (theme-aware)
  const getThemeColors = () => {
    const rootStyles = getComputedStyle(document.documentElement)
    return {
      primary: rootStyles.getPropertyValue('--color-primary').trim() || '#00ff00',
      secondary: rootStyles.getPropertyValue('--color-secondary').trim() || '#ef4444',
      accent: rootStyles.getPropertyValue('--color-header-accent').trim() || '#0a74ff',
      text: rootStyles.getPropertyValue('--color-text').trim() || '#ffffff'
    }
  }

  const config = {
    logo: networkIcon || '/Dynamic Header Logos/MdbApp.io Default Logo.png',
    colors: getThemeColors(),
    name: networkName || 'Network'
  }

  useEffect(() => {
    // Prevent double initialization
    if (initializedRef.current) {
      console.log('NetworkLoadingScreen: Already initialized, skipping')
      return
    }
    initializedRef.current = true
    console.log('NetworkLoadingScreen: Starting initialization for', config.name)

    const canvas = canvasRef.current
    if (!canvas) {
      console.error('NetworkLoadingScreen: Canvas ref is null')
      return
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      console.error('NetworkLoadingScreen: Failed to get 2d context')
      return
    }
    
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
    console.log('NetworkLoadingScreen: Canvas initialized', canvas.width, 'x', canvas.height)

    // Function to get fresh theme colors
    const getFreshColors = () => {
      const rootStyles = getComputedStyle(document.documentElement)
      return {
        primary: rootStyles.getPropertyValue('--color-primary').trim() || '#00ff00',
        secondary: rootStyles.getPropertyValue('--color-secondary').trim() || '#ef4444',
        accent: rootStyles.getPropertyValue('--color-header-accent').trim() || '#0a74ff',
        text: rootStyles.getPropertyValue('--color-text').trim() || '#ffffff'
      }
    }

    // Starfield effect
    const stars = []
    const numStars = 200

    // Initialize stars with network colors
    for (let i = 0; i < numStars; i++) {
      const rand = Math.random()
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * canvas.width,
        size: Math.random() * 2,
        colorType: rand > 0.75 ? 'accent' : rand > 0.50 ? 'primary' : rand > 0.25 ? 'secondary' : 'text'
      })
    }

    // Starfield animation
    let lastTime = 0
    let frameCount = 0
    function draw(currentTime) {
      // Throttle to ~60fps
      if (currentTime - lastTime < 16) {
        animationRef.current = requestAnimationFrame(draw)
        return
      }
      lastTime = currentTime
      frameCount++
      
      if (frameCount === 1) {
        console.log('NetworkLoadingScreen: First frame drawn')
      }

      // Get fresh theme colors on each frame
      const currentColors = getFreshColors()

      // Clear canvas with black background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw and update stars
      for (let i = 0; i < stars.length; i++) {
        const star = stars[i]
        
        // Move star towards viewer
        star.z -= 2
        
        // Reset star if it goes past the viewer
        if (star.z <= 0) {
          star.x = Math.random() * canvas.width
          star.y = Math.random() * canvas.height
          star.z = canvas.width
          
          const rand = Math.random()
          star.colorType = rand > 0.75 ? 'accent' : rand > 0.50 ? 'primary' : rand > 0.25 ? 'secondary' : 'text'
        }
        
        // Get color based on type
        const starColor = currentColors[star.colorType]
        
        // Calculate star position and size based on depth
        const k = 128 / star.z
        const px = (star.x - canvas.width / 2) * k + canvas.width / 2
        const py = (star.y - canvas.height / 2) * k + canvas.height / 2
        const size = (1 - star.z / canvas.width) * star.size * 3
        
        // Only draw if star is on screen
        if (px >= 0 && px <= canvas.width && py >= 0 && py <= canvas.height) {
          ctx.fillStyle = starColor
          ctx.beginPath()
          ctx.arc(px, py, size, 0, Math.PI * 2)
          ctx.fill()
        }
      }

      animationRef.current = requestAnimationFrame(draw)
    }

    // Start animation
    console.log('NetworkLoadingScreen: Starting animation with', stars.length, 'stars')
    animationRef.current = requestAnimationFrame(draw)

    // Start fade out after 2.5 seconds
    timersRef.current.fade = setTimeout(() => {
      console.log('NetworkLoadingScreen: Starting fade out')
      setFadeOut(true)
    }, 2500)

    // Complete loading after 3 seconds
    timersRef.current.complete = setTimeout(() => {
      console.log('NetworkLoadingScreen: Calling onComplete')
      if (!completedRef.current && onComplete) {
        completedRef.current = true
        onComplete()
      }
    }, 3000)

    return () => {
      console.log('NetworkLoadingScreen: Cleanup called (not clearing timers)')
    }
  }, [networkName, onComplete])

  return (
    <div className={`network-loading-screen ${fadeOut ? 'fade-out' : ''}`}>
      <canvas ref={canvasRef} className="network-canvas" />
      <div className="loading-logo">
        <img 
          src={config.logo} 
          alt={`${config.name} Logo`} 
          className="network-logo-image"
        />
      </div>
    </div>
  )
}
